import React, { useState } from 'react';
import { Users, UserCheck, GraduationCap, ChevronRight } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';

interface AttendanceSelectorProps {
    onSelect: (type: 'staff' | 'teacher' | 'student') => void;
}

const AttendanceSelector: React.FC<AttendanceSelectorProps> = ({ onSelect }) => {
    const [selectedType, setSelectedType] = useState<'staff' | 'teacher' | 'student' | null>(null);

    const attendanceTypes = [
        {
            type: 'staff' as const,
            title: 'Staff Attendance',
            description: 'Take attendance for school staff members',
            icon: Users,
            color: 'from-blue-500 to-blue-600',
            bgColor: 'bg-blue-50',
            iconColor: 'text-blue-600'
        },
        {
            type: 'teacher' as const,
            title: 'Teacher Attendance',
            description: 'Take attendance for teaching staff',
            icon: UserCheck,
            color: 'from-purple-500 to-purple-600',
            bgColor: 'bg-purple-50',
            iconColor: 'text-purple-600'
        },
        {
            type: 'student' as const,
            title: 'Student Attendance',
            description: 'Take attendance for students',
            icon: GraduationCap,
            color: 'from-emerald-500 to-emerald-600',
            bgColor: 'bg-emerald-50',
            iconColor: 'text-emerald-600'
        }
    ];

    return (
        <div className="space-y-6">
            <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Attendance Type</h2>
                <p className="text-gray-600">Choose the type of attendance you want to take</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {attendanceTypes.map((item) => {
                    const Icon = item.icon;
                    const isSelected = selectedType === item.type;

                    return (
                        <div
                            key={item.type}
                            onClick={() => setSelectedType(item.type)}
                            className="cursor-pointer"
                        >
                            <Card
                                glassy={true}
                                className={`islamic-pattern transition-all duration-200 hover:shadow-lg ${isSelected ? 'ring-2 ring-emerald-500 shadow-lg' : 'hover:shadow-md'
                                    }`}
                            >
                                <div className="p-6">
                                    <div className={`w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-r ${item.color} flex items-center justify-center shadow-lg`}>
                                        <Icon className="h-8 w-8 text-white" />
                                    </div>

                                    <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                                        {item.title}
                                    </h3>

                                    <p className="text-sm text-gray-600 text-center mb-4">
                                        {item.description}
                                    </p>

                                    <div className="flex items-center justify-center">
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected
                                            ? 'border-emerald-500 bg-emerald-500'
                                            : 'border-gray-300'
                                            }`}>
                                            {isSelected && (
                                                <div className="w-2 h-2 bg-white rounded-full"></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    );
                })}
            </div>

            {selectedType && (
                <div className="flex justify-center pt-4">
                    <Button
                        onClick={() => onSelect(selectedType)}
                        className="flex items-center space-x-2 px-8 py-3"
                    >
                        <span>Continue</span>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            )}
        </div>
    );
};

export default AttendanceSelector;
